<template>
  <div
    class="w-full rounded-sm bg-white text-sm text-gray-600 shadow-xl shadow-gray-500/40 sm:min-w-64"
    data-cy="platform-contact-details"
  >
    <button
      v-if="isMobile"
      class="flex w-full justify-end p-1"
      data-cy="platform-contact-details-close"
      @click="emit('close')"
    >
      <icon-x />
    </button>
    <div class="divide-y divide-gray-200">
      <div
        v-if="fundingAdvisorDetails.name"
        class="flex w-full items-center space-x-4 p-4"
        :class="{ 'pt-0': isMobile }"
      >
        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-gray-500">
          <icon-advisor class="text-white" />
        </div>
        <div>
          <div class="font-semibold">{{ fundingAdvisorDetails.name }}</div>
          <div class="text-gray-400">{{ $t("role.fundingAdvisor") }}</div>
        </div>
      </div>
      <div v-if="fundingAdvisorDetails.telephone" class="flex items-center justify-between p-4">
        <a
          :href="`tel:${fundingAdvisorDetails.telephone}`"
          class="flex items-center space-x-4 hover:underline"
          @click="trackFundingAdvisorContactedEvent"
        >
          <icon-phone class="text-gray-500" />
          <div class="text-gray-600">{{ fundingAdvisorDetails.telephone }}</div>
        </a>
        <button @click="handleCopyPhoneToClipboard">
          <icon-clipboard />
        </button>
      </div>
      <a
        v-if="fundingAdvisorDetails.calendarLink"
        :href="fundingAdvisorDetails.calendarLink"
        class="flex w-full items-center space-x-4 p-4 hover:underline"
        target="_blank"
        rel="noopener noreferrer"
        @click="trackFundingAdvisorContactedEvent"
      >
        <icon-calendar />
        <div class="font-medium">{{ $t("profile.calendar_link") }}</div>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import * as amplitude from "@amplitude/analytics-browser";
import IconPhone from "@/components/icons/IconPhone.vue";
import IconAdvisor from "@/components/icons/IconAdvisor.vue";
import IconCalendar from "@/components/icons/IconCalendar.vue";
import IconClipboard from "@/components/icons/IconClipboard.vue";
import IconX from "@/components/icons/IconX.vue";
import { useI18n } from "vue-i18n";
import useToastState from "@/composables/state/toastState";
import useApplicationsState from "@/composables/state/applicationsState";
import { useResponsive } from "@/composables/common";
import type { FundingAdvisor } from "@/api/http/schemas/applicationSchemas";

const { advisor } = defineProps<{
  advisor: FundingAdvisor;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const { isMobile } = useResponsive();
const { selectedApplication } = useApplicationsState();
const toast = useToastState();

const fundingAdvisorName = computed(() =>
  [advisor.first_name, advisor.last_name].filter(Boolean).join(" ")
);

const fundingAdvisorDetails = computed(() => {
  const { telephone, calendar_link } = advisor;
  return {
    name: isMobile ? fundingAdvisorName.value : "",
    telephone: telephone || "",
    calendarLink: calendar_link || "",
  };
});

const handleCopyPhoneToClipboard = async () => {
  const clipboardItem = new ClipboardItem({
    ["text/plain"]: fundingAdvisorDetails.value.telephone,
  });

  await navigator.clipboard.write([clipboardItem]);

  toast.value.addMessage({
    title: t("common.copiedToClipboard"),
    type: "success",
  });
  emit("close");
};

const trackFundingAdvisorContactedEvent = () => {
  amplitude.track("Funding Advisor Contacted", {
    "Application ID": selectedApplication.value?.id ?? "",
    "Funding Advisor Name": fundingAdvisorName.value,
  });
};
</script>
